import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { IEditorProps } from './Editor';
import { StringComboBox, IStringComboOption, getOptionFromString } from 'components/inputs/StringComboBox';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorLabel } from './EditorLabel';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { IPropertyChild, getChildValueAsString } from 'components/property-editors/utils/ProviderNodeUtils';
// import './EditorCombo.less'

interface IEditorComboProps extends IEditorProps {
  className?: string;
}

/**
 * A combo box consuming a property child model.
 *
 * This is currently tolerant of configuration values being either an array of strings
 * or an array of data-pairs, since all related editor attributes are not currently
 * aligned as either.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function EditorCombo({ autoFocus, child, className, id, onChange, path }: IEditorComboProps) {
  // useMemo for sure, what useEffect working correct
  const options = useMemo(() => getOptions(child), [child]);
  const stringValue = getChildValueAsString(child);
  /**
   * Though un-clearly named, this seems to be used to avoid passing values that aren't in the list,
   * so we will this will not emit free text values or show the "clear" icon. This also seems to be
   * the main difference between "Editable" and non drop-down editors, misleadingly, "Editable"
   * will not emit non-item values.
   */
  const ignoreNulls: boolean = getEditorConfigValue(child, 'IgnoreNulls');
  /**
   * If values are different than labels presumably we cannot emit search text as a value
   * in that circumstance either.
   */
  const valuesDifferentFromLabels = labelsValuesDiffer(options);
  const allowFreeText =
    getEditorConfigValue<boolean>(child, 'AllowFreeText') && !ignoreNulls && !valuesDifferentFromLabels;

  const selectedOption = useMemo(() => {
    return options.find((op: IStringComboOption<String>) => op.value === stringValue);
  }, [options, stringValue]);

  const [currentValue, setCurrentValue] = useState<any>((selectedOption && selectedOption.value) || stringValue);

  useEffect(() => {
    const value = (selectedOption && selectedOption.value) || stringValue;
    setCurrentValue(value);
  }, [selectedOption, stringValue]);

  const validation = getEditorValidationSummary(child);
  const onValueChange = useCallback(
    (value: string | null) => {
      onChange(value, path);
      setCurrentValue(value);
    },
    [onChange, path]
  );

  return (
    <EditorLabel editor={child} path={path} htmlFor={id} className={className}>
      <StringComboBox
        autoFocus={autoFocus}
        id={id}
        options={options}
        onValueChange={onValueChange}
        currentValue={currentValue}
        validation={validation}
        allowFreeText={allowFreeText}
        hideClear={ignoreNulls || valuesDifferentFromLabels}
      />
    </EditorLabel>
  );
}

function getOptions(child: IPropertyChild) {
  return (getEditorConfigValue(child, 'values') || []).map((val: DataPair | string) => {
    return typeof val === 'string' ? getOptionFromString(val) : { text: val.Name, value: val.OutputValue };
  });
}

function labelsValuesDiffer(options: IStringComboOption<string>[]) {
  return !!options.find(o => o.text !== o.value);
}
